"use client";

import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import useUserStore from "@/features/auth/store/useUserStore";

interface TooManyRequestsElementProps {
  title?: string;
  description?: string;
  retryAfter?: number;
}

const TooManyRequests = ({ title, description, retryAfter }: TooManyRequestsElementProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common"]);
  const { user } = useUserStore();

  const defaultTitle = t("common:errors.rateLimitTitle", "Rate Limit Exceeded");
  const defaultDescription = t(
    "common:errors.rateLimitDescription",
    "You've made too many requests. Please wait a moment before trying again.",
  );

  if (user) {
    navigate(-1);
  }

  return (
    <div className="bg-background flex min-h-[calc(100dvh-4rem)] w-full items-center justify-center p-4">
      <Card className="bg-foreground/50 relative mx-auto w-full max-w-xl overflow-hidden rounded-lg border-none p-20 backdrop-blur-xl">
        <div className="absolute inset-0 opacity-35">
          <div className="absolute -top-40 -left-40 size-80 rounded-full bg-gradient-to-br from-red-600 to-red-800" />
          <div className="absolute -right-30 -bottom-30 size-60 rounded-full bg-gradient-to-tr from-red-500 to-red-600" />
        </div>
        <CardContent className="relative z-50 flex flex-col items-center p-8 text-center">
          <motion.div
            animate={{ rotate: 360 }}
            className="bg-primary/10 mb-6 flex h-24 w-24 items-center justify-center rounded-full"
            initial={{ rotate: 0 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              repeatDelay: 0.33,
              ease: "easeInOut",
            }}
          >
            <Icon
              className="text-muted-foreground"
              height={"3rem"}
              icon="mdi:timer-sand"
              width={"3rem"}
            />
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="space-y-2">
              <h1 className="text-accent-foreground text-3xl font-bold">{title || defaultTitle}</h1>
              <p className="text-muted-foreground">{description || defaultDescription}</p>
              {retryAfter && (
                <p className="text-muted-foreground mt-2">
                  {t("common:errors.retryAfter", "Please try again in {{seconds}} seconds", {
                    seconds: retryAfter,
                  })}
                </p>
              )}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                className="flex w-full items-center gap-2"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="h-4 w-4" />
                {t("common:goBack")}
              </Button>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TooManyRequests;
